/* eslint-disable react-hooks/exhaustive-deps */
// src/Pages/PreOrder/Camera/hooks/useCamera.js
import { useState, useRef, useEffect } from "react";

const useCamera = () => {
  // States
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);
  const [isFrontCamera, setIsFrontCamera] = useState(true);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [currentResolution, setCurrentResolution] = useState(null);

  // Refs
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const isStartingRef = useRef(false);

  // Clean up stream
  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (e) {
          console.error("Error stopping track:", e);
        }
      });
      streamRef.current = null;
    }
    setIsReady(false);
  };
  // Get maximum capabilities of a specific camera
  const getCameraCapabilities = async (deviceId) => {
    try {
      // Get stream with the specific camera
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } },
      });

      const videoTrack = stream.getVideoTracks()[0];
      const capabilities = videoTrack.getCapabilities();

      // Clean up temporary stream
      stream.getTracks().forEach((track) => track.stop());


      return {
        maxWidth: capabilities.width?.max || 1920,
        maxHeight: capabilities.height?.max || 1080,
        minWidth: capabilities.width?.min || 320,
        minHeight: capabilities.height?.min || 240,
        aspectRatio: capabilities.aspectRatio,
      };
    } catch (err) {
      console.error("Error getting camera capabilities:", err);
      return null;
    }
  };

  // Start camera with maximum quality for specific camera
  const startCamera = async () => {
    if (isStartingRef.current) return false;

    isStartingRef.current = true;
    setError(null);
    stopStream();

    try {
      let constraints;
      let selectedCamera = null; // If cameras are available, select the appropriate one
      if (availableCameras.length > 0) {
        // Find front or back camera according to choice
        selectedCamera = availableCameras.find((camera) => {
          const label = camera.label.toLowerCase();
          if (isFrontCamera) {
            return (
              label.includes("front") ||
              label.includes("user") ||
              label.includes("selfie") ||
              label.includes("facetime")
            ); // For iOS case
          } else {
            return (
              label.includes("back") ||
              label.includes("rear") ||
              label.includes("environment")
            );
          }
        }); // If not found by label, take by index
        if (!selectedCamera) {
          // On phones: camera 0 = back, camera 1 = front (usually)
          // On computers: usually only one camera
          const cameraIndex = isFrontCamera
            ? availableCameras.length > 1
              ? 1
              : 0
            : 0;
          selectedCamera = availableCameras[cameraIndex];
        }

      } // Try first with getCapabilities if available
      let maxCapabilities = null;
      if (selectedCamera && "getCapabilities" in MediaStreamTrack.prototype) {
        maxCapabilities = await getCameraCapabilities(selectedCamera.deviceId);
      }

      if (maxCapabilities && selectedCamera) {
        // Limit resolution to Full HD for better performance
        const maxAllowedWidth = 1920;
        const maxAllowedHeight = 1080;

        constraints = {
          video: {
            deviceId: { exact: selectedCamera.deviceId },
            width: {
              ideal: Math.min(maxCapabilities.maxWidth, maxAllowedWidth),
              max: maxAllowedWidth,
            },
            height: {
              ideal: Math.min(maxCapabilities.maxHeight, maxAllowedHeight),
              max: maxAllowedHeight,
            },
            frameRate: { ideal: 30, max: 30 },
          },
        };
      } else {
        // Limit to Full HD in the general case too
        constraints = {
          video: {
            facingMode: isFrontCamera ? "user" : "environment",
            width: { ideal: 1920, max: 1920 },
            height: { ideal: 1080, max: 1080 },
            frameRate: { ideal: 30, max: 30 },
          },
        };
      }

      // Get the stream
      let stream = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (err) {
        console.warn("נכשל עם constraints מתקדמים, מנסה בסיסי...", err);
        // Basic fallback attempt
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: isFrontCamera ? "user" : "environment",
          },
        });
      }

      if (!stream) {
        throw new Error("Cannot access camera");
      }

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;

        // Wait for video to be ready
        await new Promise((resolve, reject) => {
          const video = videoRef.current;
          let resolved = false;

          const handleSuccess = () => {
            if (!resolved) {
              resolved = true;

              // Get actual resolution from video element
              const actualWidth = video.videoWidth;
              const actualHeight = video.videoHeight;
              const aspectRatio = (actualWidth / actualHeight).toFixed(2);


              // Save current resolution data
              setCurrentResolution({
                width: actualWidth,
                height: actualHeight,
                aspectRatio: aspectRatio,
                megapixels: ((actualWidth * actualHeight) / 1000000).toFixed(1),
              });

              setIsReady(true);
              resolve();
            }
          };

          const handleError = (err) => {
            if (!resolved) {
              resolved = true;
              reject(err);
            }
          };

          // Multiple event listeners for compatibility
          video.addEventListener("loadedmetadata", handleSuccess, {
            once: true,
          });
          video.addEventListener("loadeddata", handleSuccess, { once: true });
          video.addEventListener("canplay", handleSuccess, { once: true });
          video.addEventListener("playing", handleSuccess, { once: true });
          video.addEventListener("error", handleError, { once: true });

          // Timeout fallback
          setTimeout(() => {
            if (!resolved) {
              if (video.readyState >= 2) {
                handleSuccess();
              } else {
                handleError(new Error("Video timeout"));
              }
            }
          }, 3000);

          // Start playing
          video
            .play()
            .then(() => {
              setTimeout(() => {
                if (!resolved && video.readyState >= 2) {
                  handleSuccess();
                }
              }, 100);
            })
            .catch(handleError);
        });
      }

      isStartingRef.current = false;
      return true;
    } catch (err) {
      console.error("Camera error:", err);
      isStartingRef.current = false;

      if (err.name === "NotAllowedError") {
        setError("נדרשת הרשאה לשימוש במצלמה");
      } else if (err.name === "NotFoundError") {
        setError("לא נמצאה מצלמה");
      } else if (err.name === "NotReadableError") {
        setError("המצלמה בשימוש באפליקציה אחרת");
      } else {
        setError("שגיאה בהפעלת המצלמה");
      }

      return false;
    }
  };

  // Switch camera
  const switchCamera = () => {
    if (!isStartingRef.current && availableCameras.length > 1) {
      setIsFrontCamera((prev) => !prev);
    }
  };

  // Get cameras list
  const initializeCameras = async () => {
    try {
      // Request initial permission for camera access
      const tempStream = await navigator.mediaDevices.getUserMedia({
        video: true,
      });

      // Get list of available cameras
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((device) => device.kind === "videoinput");


      // Clean up temporary stream
      tempStream.getTracks().forEach((track) => track.stop());

      setAvailableCameras(cameras);

      // Ensure we start with front camera
      setIsFrontCamera(true);

      return cameras.length > 0;
    } catch (err) {
      console.error("Permission error:", err);
      setError("נדרשת הרשאה לשימוש במצלמה");
      return false;
    }
  };

  // Capture photo with maximum quality
  const capturePhoto = () => {
    if (!isReady || !videoRef.current) return null;

    const video = videoRef.current;
    const canvas = document.createElement("canvas");

    // Use full video resolution for capture
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;


    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    // Mirror front camera
    if (isFrontCamera) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Export with maximum quality
    // PNG for maximum quality, but file may be large
    // Alternative: JPEG with high quality: canvas.toDataURL("image/jpeg", 0.95)
    return canvas.toDataURL("image/png");
  };

  // Initialize
  useEffect(() => {
    const init = async () => {
      const hasPermission = await initializeCameras();
      if (hasPermission) {
        // Wait for video element
        const checkVideoElement = () => {
          if (videoRef.current) {
            startCamera();
          } else {
            setTimeout(checkVideoElement, 100);
          }
        };
        checkVideoElement();
      }
    };

    init();

    return () => {
      stopStream();
    };
  }, []);

  // Handle camera switch
  useEffect(() => {
    if (availableCameras.length > 0 && !isStartingRef.current) {
      startCamera();
    }
  }, [isFrontCamera]);

  return {
    // States
    isReady,
    error,
    isFrontCamera,
    availableCameras,
    currentResolution,

    // Refs
    videoRef,

    // Actions
    switchCamera,
    capturePhoto,
    startCamera,
    stopStream,
  };
};

export default useCamera;
